import React, { useState, useRef, useImperativeHandle } from 'react';

import ChipOptionButton from './chipSelect/ChipOptionButton';
import { ChipSelectWrapper } from './chipSelect/ChipSelect.styles';
import { ChipSelectData, canAutoAdvance } from './chipSelect/chipSelectTypes';
import { toValueList } from './chipSelect/chipSelectUtils';
import { useAutoAdvance } from './chipSelect/useAutoAdvance';

export type {
  ChipSelectOption,
  ChipSelectCustomValueType,
} from './chipSelect/chipSelectTypes';

interface ChipSelectProps {
  data: ChipSelectData;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  mutable?: boolean;
  defaultValue?: string | string[];
}

const ChipSelectInput = React.forwardRef<HTMLInputElement, ChipSelectProps>(
  (props, ref) => {
    const data = props.data || {};
    const {
      field_name,
      required,
      options = [],
      multiple,
      layout = 'chips',
      allowCustomValue,
      customValuePlaceholder,
      customValueType = 'text',
      value: initialValue,
    } = data;

    const isLiveForm = props.mutable !== false;
    const autoAdvance = Boolean(data.autoAdvance) && canAutoAdvance(data);
    const scheduleAdvance = useAutoAdvance(isLiveForm && autoAdvance);

    const [selected, setSelected] = useState<string[]>(
      toValueList(props.defaultValue ?? initialValue),
    );
    const [touched, setTouched] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);


    const mirrorsChips = Boolean(allowCustomValue) && !multiple;
    const [customValue, setCustomValue] = useState<string>(() =>
      mirrorsChips ? toValueList(props.defaultValue ?? initialValue).join('') : '',
    );

    const answerOf = (chips: string[], custom: string): string => {
      if (mirrorsChips) return custom;
      const extra = custom.trim();
      if (!allowCustomValue || !extra) return chips.join(',');
      return [...chips, extra].join(',');
    };

    const isEmpty = mirrorsChips
      ? customValue.trim() === ''
      : selected.length === 0 && customValue.trim() === '';
    const showError = isLiveForm && touched && required && isEmpty;

    const emit = (chips: string[], custom: string) => {
      setTouched(true);
      if (props.onChange && field_name) {
        props.onChange({ [field_name]: answerOf(chips, custom) });
      }
    };

    const emitChange = (next: string[]) => {
      setSelected(next);
      const nextCustom = mirrorsChips ? next.join('') : customValue;
      if (mirrorsChips) setCustomValue(nextCustom);
      emit(next, nextCustom);
    };

    const onCustomValueChange = (value: string) => {
      setCustomValue(value);
      const nextSelected = mirrorsChips
        ? options
            .filter((option) => option.value === value.trim())
            .map((option) => option.value)
        : selected;
      if (mirrorsChips) setSelected(nextSelected);
      emit(nextSelected, value);
    };

    const toggle = (value: string) => {
      if (!multiple) {
        /* With auto-advance, re-clicking the current answer confirms it
           (e.g. after stepping back) instead of clearing it. */
        const keep = autoAdvance || !selected.includes(value);
        emitChange(keep ? [value] : []);
        if (keep) scheduleAdvance();
        return;
      }
      if (selected.includes(value)) {
        emitChange(selected.filter((v) => v !== value));
        return;
      }
      const exclusiveValues = options
        .filter((option) => option.exclusive)
        .map((option) => option.value);
      emitChange(
        exclusiveValues.includes(value)
          ? [value]
          : [...selected.filter((v) => !exclusiveValues.includes(v)), value],
      );
    };

    const interactive = isLiveForm && !props.read_only;

    return (
      <ChipSelectWrapper
        className={`chip-select-layout-${layout}${isLiveForm ? ' is-live' : ''}${
          showError ? ' has-error' : ''
        }`}
      >
        <div
          className="chip-select-list"
          role={multiple ? 'group' : 'radiogroup'}
        >
          {options.map((option) => (
            <ChipOptionButton
              key={option.key || option.value}
              option={option}
              layout={layout}
              multiple={multiple}
              isSelected={selected.includes(option.value)}
              interactive={interactive}
              onToggle={toggle}
            />
          ))}
        </div>

        {allowCustomValue &&
          (customValueType === 'textarea' ? (
            <textarea
              className="chip-select-custom-input"
              rows={3}
              placeholder={
                customValuePlaceholder ||
                (mirrorsChips ? 'Enter a value' : 'Add another value')
              }
              value={customValue}
              disabled={!interactive}
              onChange={(e) => onCustomValueChange(e.target.value)}
              onBlur={() => setTouched(true)}
            />
          ) : (
            <input
              type={customValueType}
              className="chip-select-custom-input"
              placeholder={
                customValuePlaceholder ||
                (mirrorsChips ? 'Enter a value' : 'Add another value')
              }
              value={customValue}
              disabled={!interactive}
              onChange={(e) => onCustomValueChange(e.target.value)}
              onBlur={() => setTouched(true)}
            />
          ))}

        {showError && (
          <div className="chip-select-error-text">This field is required.</div>
        )}
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={field_name}
          value={answerOf(selected, customValue)}
        />
      </ChipSelectWrapper>
    );
  },
);

ChipSelectInput.displayName = 'ChipSelectInput';

export default ChipSelectInput;
